
import { useState, useEffect } from 'react';
import type { Tool } from '@/data/tools';

interface FilterOptions {
  allTools: Tool[];
  categories: { id: string, name: string }[];
  sports: { id: string, name: string }[];
}

interface FilterHookReturn {
  selectedCategoryIds: string[];
  selectedSportId: string | null;
  handleCategorySelect: (categoryId: string) => void;
  handleSportSelect: (sportId: string | null) => void;
  filteredTools: Tool[];
}

export const useToolFiltering = ({ allTools, categories, sports }: FilterOptions): FilterHookReturn => {
  const [selectedCategoryIds, setSelectedCategoryIds] = useState<string[]>([]);
  const [selectedSportId, setSelectedSportId] = useState<string | null>(null);
  const [filteredTools, setFilteredTools] = useState<Tool[]>(allTools);

  // Handle category selection
  const handleCategorySelect = (categoryId: string) => {
    setSelectedCategoryIds(prev => {
      if (prev.includes(categoryId)) {
        return prev.filter(id => id !== categoryId);
      } else {
        return [...prev, categoryId];
      }
    });
  };
  
  // Handle sport selection
  const handleSportSelect = (sportId: string | null) => {
    setSelectedSportId(sportId);
  };
  
  // Apply filters effect
  useEffect(() => {
    if (allTools.length === 0) return;
    
    let filtered = [...allTools];
    
    // Filter by categories if any selected
    if (selectedCategoryIds.length > 0) {
      filtered = filtered.filter(tool => {
        const toolCategory = tool.category;
        if (!toolCategory) return false;
        
        // Find the category object that matches the tool's category name
        const categoryObj = categories.find(c => c.name === toolCategory);
        if (!categoryObj) return false;
        
        return selectedCategoryIds.includes(categoryObj.id);
      });
    }
    
    // Filter by sport if selected
    if (selectedSportId) {
      const sportName = sports.find(s => s.id === selectedSportId)?.name;
      filtered = filtered.filter(tool => 
        tool.sports.some(s => s === sportName)
      );
    }
    
    setFilteredTools(filtered);
  }, [allTools, selectedCategoryIds, selectedSportId, categories, sports]);

  return {
    selectedCategoryIds,
    selectedSportId,
    handleCategorySelect,
    handleSportSelect,
    filteredTools
  };
};
